import { useAtomValue } from 'jotai';
import { SyntheticEvent, useCallback, useEffect, useMemo, useState, type JSX } from 'react';

import { FormField, FormFieldsContainer, FormLabelText } from '../../../../components/Form/FormComponents';
import { StickyFormButtons } from '../../../../components/Form/StickyFormButtons';
import { MuiMultiselect } from '../../../../components/MuiMultiselect/MuiMultiselect';
import { Option } from '../../../../components/MuiSingleSelect/MuiSingleSelect';
import { activeUsersAtom } from '../../../../services/state/AppConfigStateJ';
import { activePortcoUsersAtom } from '../../../../services/state/UsersStateJ';
import { SharedButtonStatus, TSharedUser } from '../../../../types';
import { useShareView } from '../../hooks/useViewActions';
import { selectedViewPFJ } from '../../state/ViewStateJ';
import { SharedUser } from './SharedUser';

interface IShareViewModalProps {
  onCloseClick: () => void;
}

export const ShareViewModal = ({ onCloseClick }: IShareViewModalProps): JSX.Element => {
  const view = useAtomValue(selectedViewPFJ);
  const shareView = useShareView();
  const [tags, setTags] = useState<Option[]>([]);
  const [sharedUsers, setSharedUsers] = useState<TSharedUser[]>([]);
  const [owner, setOwner] = useState<TSharedUser | null>(null);
  const [loading, setLoading] = useState(false);

  const portcoUsers = useAtomValue(activePortcoUsersAtom);

  // All active users (loaded synchronously at app startup) are needed to resolve
  // existing share recipients and the owner, since prior shares may include users
  // outside the portco type.
  const allUsers = useAtomValue(activeUsersAtom);

  const handleChange = useCallback((e: SyntheticEvent, value: Option[] | undefined) => {
    if (!value) {
      return;
    }
    setTags(value);
  }, []);

  const onShare = () => {
    if (!tags || !tags.length || !view) {
      return;
    }
    const emails = tags.map((t) => t.id as string);

    setLoading(true);

    shareView(view, emails).then(() => {
      setLoading(false);
      setTags([]);
      onCloseClick();
    });
  };

  const onCancel = () => {
    onCloseClick();
  };

  // Resolve existing share recipients against all users so that non-portco users are
  // not silently dropped. Falls back to displaying the raw email when a match is
  // not found (e.g. a deactivated or deleted account).
  useEffect(() => {
    if (!view?.sharing) {
      return;
    }
    const newSharedUsers: TSharedUser[] = view.sharing.map((email) => {
      const user = allUsers.find((u) => u.email === email);
      return {
        id: user?.email ?? email,
        value: user ? `${user.name} (${user.email})` : email,
        type: SharedButtonStatus.shared,
      };
    });
    setSharedUsers(newSharedUsers);
  }, [view, allUsers]);

  // Resolve the view owner against all users for the same reason.
  useEffect(() => {
    if (!view?.createdBy) {
      return;
    }
    const user = allUsers.find((u) => u.email === view.createdBy);
    setOwner({
      id: view.createdBy,
      value: user ? `${user.name} (${user.email})` : view.createdBy,
      type: SharedButtonStatus.owner,
    });
  }, [view, allUsers]);

  const options = useMemo(
    () =>
      portcoUsers
        .filter((u) => sharedUsers.every((su) => su.id !== u.email))
        .filter((u) => owner?.id !== u.email)
        .map((u) => ({ id: u.email, value: `${u.name} (${u.email})` })),
    [owner?.id, sharedUsers, portcoUsers]
  );

  return (
    <>
      <FormFieldsContainer>
        <FormField>
          <FormLabelText label='With' required />
          <MuiMultiselect
            options={options}
            value={tags}
            onChange={handleChange}
            style={{ width: '100%' }}
            fieldPlaceholder={'Add people by entering emails or names'}
            disablePortal={false}
          />
        </FormField>
        <>
          {owner && <SharedUser item={owner} setSharedUsers={setSharedUsers} />}
          {sharedUsers.map((user) => (
            <SharedUser key={user.id} item={user} setSharedUsers={setSharedUsers} />
          ))}
        </>
      </FormFieldsContainer>
      <StickyFormButtons
        onSubmit={onShare}
        onCancel={onCancel}
        submitLabel='Share'
        loading={loading}
        disabled={false}
      />
    </>
  );
};
